import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import VintageLayout from '../components/layout/VintageLayout';
import { Mail } from 'lucide-react';

export const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await api.post('/auth/forgot-password', { email });
            setEmailSent(true);
            toast.success('Email de réinitialisation envoyé !');
        } catch (error: any) {
            const message = error.response?.data?.message || 'Erreur lors de l\'envoi';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    if (emailSent) {
        return (
            <VintageLayout>
                <div className="min-h-screen flex items-center justify-center p-6">
                    <div className="w-full max-w-md relative z-10 animate-fade-in-up p-8 rounded-2xl border border-gold-500/30 bg-navy-900/60 backdrop-blur-md shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-green-500/30">
                                <Mail size={32} className="text-green-400" />
                            </div>
                            <h2 className="text-3xl font-serif font-bold text-cream-100 mb-4">Email envoyé !</h2>
                            <p className="text-cream-200/80 mb-8 leading-relaxed">
                                Un lien de réinitialisation a été envoyé à <span className="text-gold-400 font-bold">{email}</span>.
                                <br />
                                <br />
                                Vérifiez votre boîte de réception et suivez les instructions.
                            </p>
                            <button
                                onClick={() => navigate('/login')}
                                className="btn-vintage w-full py-3"
                            >
                                Retour à la connexion
                            </button>
                        </div>
                    </div>
                </div>
            </VintageLayout>
        );
    }

    return (
        <VintageLayout>
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="w-full max-w-md relative z-10 animate-fade-in-up p-8 rounded-2xl border border-gold-500/30 bg-navy-900/60 backdrop-blur-md shadow-[0_0_30px_rgba(0,0,0,0.5)]">
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-6">
                            <img
                                src="/assets/logo-text.png"
                                alt="Réminiscence"
                                className="h-20 w-auto brightness-0 invert drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]"
                            />
                        </div>
                        <h2 className="text-3xl font-serif font-bold text-cream-100 mb-2">Mot de passe oublié</h2>
                        <p className="text-cream-200/60 italic font-serif">Retrouvez l'accès à vos souvenirs</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block mb-2 text-gold-100 font-semibold font-serif">Adresse Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-navy-950/50 border border-gold-500/30 rounded-lg px-4 py-3 text-cream-100 placeholder-navy-400 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-all font-serif"
                                placeholder="Entrez votre email..."
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-vintage w-full flex justify-center items-center gap-2 group py-3 text-lg disabled:opacity-50"
                        >
                            <span>{isLoading ? 'Envoi en cours...' : 'Envoyer le lien'}</span>
                            {!isLoading && <span className="group-hover:translate-x-1 transition-transform">→</span>}
                        </button>
                    </form>

                    <div className="mt-8 text-center pt-6 border-t border-gold-500/20">
                        <p className="text-cream-200/60">
                            Vous vous souvenez ?{' '}
                            <Link to="/login" className="text-gold-400 font-bold hover:text-gold-300 transition-colors font-serif underline decoration-gold-500/50 decoration-2 underline-offset-4">
                                Se connecter
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </VintageLayout>
    );
};

export default ForgotPasswordPage;
